import { useCallback, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import NewsletterPopup from "@/components/NewsletterPopup";
import useSEO from "@/lib/useSEO";

export default function Save10() {
  useSEO({
    title: "Save 10% on Your First Melbi Order",
    description:
      "Receive 10% off your first wellness prescription. Join the Melbi list to unlock your welcome code, plus be the first to hear about new offerings and promotions.",
    url: "https://www.melbiwellness.com/save10",
  });

  const navigate = useNavigate();
  const [showLandingCTA, setShowLandingCTA] = useState(false);

  // When the popup is dismissed or the "Close" button is clicked after a
  // successful subscribe, show a small landing CTA that lets the visitor
  // continue browsing rather than staring at a static page.
  const handleClose = useCallback(() => {
    setShowLandingCTA(true);
  }, []);

  return (
    <div
      data-testid="save10-page"
      className="min-h-screen bg-[#FAF9F6] flex flex-col"
    >
      {/* Minimal branded header */}
      <header className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-5 flex items-center justify-between">
        <Link to="/" aria-label="Melbi Wellness home">
          <Logo />
        </Link>
        <Link
          to="/"
          className="text-xs sm:text-sm font-semibold text-[#5E5C58] hover:text-[#2C2B29] transition-colors"
          data-testid="save10-return-home-link"
        >
          Visit melbiwellness.com →
        </Link>
      </header>

      {/* Landing context (visible behind the popup + after close) */}
      <main className="flex-1 flex items-center justify-center px-4 py-10 sm:py-14">
        <div className="max-w-xl w-full text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8A9A92]">
            Exclusive Offer
          </p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight text-[#2C2B29]">
            Receive <span className="text-[#C96A4E]">10% off</span> your first
            wellness prescription.
          </h1>
          <p className="mt-4 text-[#5E5C58] leading-relaxed text-sm sm:text-base">
            Enter your name and email to unlock your welcome offer and be the
            first to hear about new Melbi wellness offerings and exclusive
            promotions.
          </p>

          {showLandingCTA && (
            <div className="mt-8" data-testid="save10-continue-block">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-7 py-3.5 text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm"
                data-testid="save10-continue-home-cta"
              >
                Explore Melbi Wellness
                <ArrowRight size={16} className="ml-2" />
              </button>
              <p className="mt-3 text-xs text-[#8A9A92]">
                Check your inbox — your welcome offer is on its way.
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-[#E8E5DF] py-4 text-center text-[11px] text-[#8A9A92]">
        © {new Date().getFullYear()} Melbi Wellness. Prescription treatments
        require clinician review. Not all patients qualify.
      </footer>

      {/* Auto-open the existing popup on mount, override its close behavior */}
      <NewsletterPopup forceOpen onClose={handleClose} />
    </div>
  );
}
